/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/* eslint-disable @typescript-eslint/no-unsafe-argument */

// TODO: Fix ESLint errors and improve type safety

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type {
  PricingDynamicPriceEntry,
  Currency,
} from '../../types/charge-point.types';
import {
  generateSpotPriceHourSpan,
  formatPrice,
  getCurrencyDisplay,
} from '../../utils/price-utils';

interface DynamicPriceForecastProps {
  entries: PricingDynamicPriceEntry[];
  currency: Currency;
}

/**
 * Display dynamic price forecast with hourly price entries
 * Shows both chart and list views with a toggle
 */
export function DynamicPriceForecast({
  entries,
  currency,
}: DynamicPriceForecastProps) {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('chart');

  if (!entries || entries.length === 0) {
    return null;
  }

  // Prepare data for chart
  const chartData = entries.map((entry) => {
    const date = new Date(entry.time);
    const hour = date.getHours();
    return {
      hour: hour,
      label: `${hour}:00`,
      price: entry.value,
      time: entry.time,
    };
  });

  // Find min/max for color coding
  const prices = entries.map((e) => e.value);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface p-2 border border-ink/15 rounded shadow-lg">
          <p className="text-xs text-ink/80">
            {generateSpotPriceHourSpan(data.time, i18n.language)}
          </p>
          <p className="text-sm font-bold text-ink">
            {formatPrice(data.price, currency, i18n.language)}{' '}
            {getCurrencyDisplay(currency)}/kWh
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="border-l-2 border-orange-500 pl-3 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="font-medium text-sm text-ink">
          {t('chargePoint.dynamicPriceForecast')}
        </h5>

        {/* View toggle buttons */}
        <div className="flex items-center gap-1 bg-ink/5 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('chart')}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              viewMode === 'chart'
                ? 'bg-surface text-ink shadow-sm'
                : 'text-muted hover:text-ink'
            }`}
            aria-label={t('chargePoint.chartView')}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-surface text-ink shadow-sm'
                : 'text-muted hover:text-ink'
            }`}
            aria-label={t('chargePoint.listView')}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div className="w-full h-48 text-muted">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
            >
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'currentColor' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'currentColor' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) =>
                  formatPrice(value, currency, i18n.language)
                }
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(127, 127, 127, 0.12)' }}
              />
              <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => {
                  // Color code: green for low, orange for mid, red for high
                  let fill = '#F97316'; // Default orange
                  const priceRange = maxPrice - minPrice;
                  if (priceRange > 0) {
                    const normalizedPrice =
                      (entry.price - minPrice) / priceRange;
                    if (normalizedPrice < 0.33) {
                      fill = '#10B981'; // Green for low prices
                    } else if (normalizedPrice > 0.66) {
                      fill = '#EF4444'; // Red for high prices
                    }
                  }
                  return <Cell key={`cell-${index}`} fill={fill} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {entries.map((entry, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-ink/80">
                {generateSpotPriceHourSpan(entry.time, i18n.language)}
              </span>
              <span className="text-sm">
                <span className="font-bold text-ink">
                  {formatPrice(entry.value, currency, i18n.language)}
                </span>
                <span className="text-ink/80">
                  {' '}
                  {getCurrencyDisplay(currency)}/kWh
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
